// src/DocumentUpload.tsx
import React, { useState } from 'react';
import { PhotoCropper } from './PhotoCropper';

interface DocumentUploadProps {
  countryCode?: 'INDIA' | 'MALAYSIA' | 'VIETNAM' | string;
  onPhotoUpload?: (base64: string) => void;
  onPassportUpload?: (base64: string) => void;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  countryCode = 'INDIA',
  onPhotoUpload,
  onPassportUpload,
}) => {
  const [photoInfo, setPhotoInfo] = useState<{ name: string; sizeKB: number; preview: string; error?: string } | null>(null);
  const [passportInfo, setPassportInfo] = useState<{ name: string; sizeKB: number; preview?: string; error?: string } | null>(null);

  const country = countryCode.toUpperCase();

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeKB = Math.round(file.size / 1024);
    const maxKB = country === 'VIETNAM' ? 2048 : 1024;
    if (sizeKB > maxKB) {
      setPhotoInfo({ name: file.name, sizeKB, preview: '', error: `File size (${sizeKB} KB) exceeds limit (${maxKB / 1024} MB max)!` });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setPhotoInfo({ name: file.name, sizeKB, preview: base64 });
      if (onPhotoUpload) onPhotoUpload(base64);
    };
    reader.readAsDataURL(file);
  };

  const handlePassportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeKB = Math.round(file.size / 1024);
    const maxKB = country === 'VIETNAM' ? 2048 : 300;
    if (sizeKB > maxKB) {
      setPassportInfo({
        name: file.name,
        sizeKB,
        error: `File size (${sizeKB} KB) exceeds limit (${maxKB > 1000 ? (maxKB / 1024) + ' MB' : maxKB + ' KB'})! Please compress file.`,
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setPassportInfo({ name: file.name, sizeKB, preview: file.type.includes('image') ? base64 : undefined });
      if (onPassportUpload) onPassportUpload(base64);
    };
    reader.readAsDataURL(file);
  };

  const getPhotoRulesText = () => {
    if (country === 'VIETNAM') {
      return 'JPEG/PNG | 4x6 cm | Max 2 MB | Straight Face, White Background';
    }
    if (country === 'MALAYSIA') {
      return 'JPEG/PNG | 35x50 mm | Max 1 MB | White Background';
    }
    return 'JPEG/PNG | Min 10 KB, Max 1 MB | 1:1 Square White Background';
  };

  const getPassportRulesText = () => {
    if (country === 'VIETNAM') {
      return 'PDF/JPEG | Clear Passport Data Page, No Lost Corners | Max 2 MB';
    }
    return 'PDF/JPEG | Clear Passport Bio Data Page | 10 KB - 300 KB';
  };

  return (
    <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '1.1rem', color: '#1e3a8a', fontWeight: '700', marginBottom: '1rem' }}>
        📎 Document Upload & Photo Cropper Helper ({country})
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        {/* Photo Upload Card */}
        <div
          style={{
            border: '2px dashed #cbd5e1',
            borderRadius: '12px',
            padding: '1.25rem',
            textAlign: 'center',
            background: '#ffffff',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📸</div>
          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>Applicant Photograph</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            {getPhotoRulesText()}
          </div>

          <label
            style={{
              display: 'inline-block',
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#eff6ff',
              color: '#1d4ed8',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              border: '1px solid #bfdbfe',
            }}
          >
            Choose Photo File
            <input type="file" accept="image/jpeg,image/png" onChange={handlePhotoChange} style={{ display: 'none' }} />
          </label>

          {photoInfo && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', textAlign: 'left' }}>
              {photoInfo.error ? (
                <div style={{ color: '#ef4444', fontWeight: '600' }}>⚠️ {photoInfo.error}</div>
              ) : (
                <div style={{ color: '#16a34a', fontWeight: '600' }}>
                  ✅ {photoInfo.name} ({photoInfo.sizeKB} KB)
                  {photoInfo.preview && (
                    <img
                      src={photoInfo.preview}
                      alt="Preview"
                      style={{ display: 'block', maxHeight: '100px', margin: '0.5rem auto 0', borderRadius: '4px' }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Passport Bio Page Upload Card */}
        <div
          style={{
            border: '2px dashed #cbd5e1',
            borderRadius: '12px',
            padding: '1.25rem',
            textAlign: 'center',
            background: '#ffffff',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📘</div>
          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>Passport Bio Page Scan</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
            {getPassportRulesText()}
          </div>

          <label
            style={{
              display: 'inline-block',
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              background: '#eff6ff',
              color: '#1d4ed8',
              borderRadius: '6px',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              border: '1px solid #bfdbfe',
            }}
          >
            Choose Passport File
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={handlePassportChange}
              style={{ display: 'none' }}
            />
          </label>

          {passportInfo && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', textAlign: 'left' }}>
              {passportInfo.error ? (
                <div style={{ color: '#ef4444', fontWeight: '600' }}>⚠️ {passportInfo.error}</div>
              ) : (
                <div style={{ color: '#16a34a', fontWeight: '600' }}>
                  ✅ {passportInfo.name} ({passportInfo.sizeKB} KB)
                  {passportInfo.preview && (
                    <img
                      src={passportInfo.preview}
                      alt="Preview"
                      style={{ display: 'block', maxHeight: '100px', margin: '0.5rem auto 0', borderRadius: '4px' }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <PhotoCropper countryCode={countryCode} />
    </div>
  );
};
